import React, { useState } from 'react';
import Layout from '../../components/layout/layout';
import Button from '../../components/ui/button';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import SectionBackground from '../../components/sections/section-background';
import Breadcrumb from '../../components/ui/breadcrumb';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import Section from '../../components/sections/section';
import HubSpotModal from '../../components/ui/hubspot-modal';

export default function ExternalisationPage() {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);

  return (
    <Layout>
      <WebPageSchema
        page={{
          title: "Externalisation RH à Marseille : paie, conformité, onboarding",
          description: "Externalisez vos RH à Marseille avec Recruteurs.io. Audit gratuit, conformité, paie, onboarding, outils IA. Un accompagnement RH moderne et structuré.",
          url: "/entreprise/externalisation"
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: '/' },
          { name: 'Entreprise', url: '/entreprise' },
          { name: 'Externalisation', url: '/entreprise/externalisation' }
        ]}
      />

      <HeroTemplateEnts
        title="Externalisez vos RH à Marseille en toute confiance"
        description="De la mise en conformité à la gestion de la paie, en passant par l'onboarding ou les recrutements en urgence, notre équipe vous accompagne avec des outils modernes et des résultats mesurables."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Vivier-de-talents.webp"
        page="externalisation"
        breadcrumbs={[
          { name: 'Entreprise', href: '/entreprise' },
          { name: 'Externalisation', href: '/entreprise/externalisation' }
        ]}
        cta1={{
          label: "Planifier votre audit RH gratuit",
          href: "https://calendar.app.google/ehHQymdfUYD7baYC9"
        }}
        cta2={{
          label: "En savoir plus",
          href: "/infos/contact"
        }}
      />

      <Section
        title="Mise en conformité RH à Marseille"
        description="Droit du travail, affichage obligatoire, DUERP… On s'occupe de tout. Évitez les sanctions (jusqu'à 3750€ par manquement constaté) avec un accompagnement local et personnalisé."
        bullets={[
          "Audit réglementaire complet",
          "Documentation à jour + archivage numérique",
          "Assistance inspection du travail"
        ]}
        image="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/mise-en-conformite-rh.webp"
        backgroundColor="bg-white"
        extraContent={
          <Button 
            variant="gradient" 
            onClick={() => setIsHubSpotModalOpen(true)}
            className="mt-8"
          >
            ⚡️ Auditer mes process
          </Button>
        }
      />

      <Section
        reversed
        title="Stratégie de rémunération & conformité paie"
        description="Nous construisons avec vous une politique de rémunération alignée avec vos objectifs. Objectif : sécuriser juridiquement vos pratiques et mieux valoriser vos talents à Marseille et en région PACA."
        bullets={[
          "Audit paie (URSSAF, égalité pro, temps de travail)",
          "Grilles salariales & P&L RH",
          "DSN & cotisations vérifiées",
          "Jusqu'à 30% d'erreurs détectées selon nos audits internes"
        ]}
        image="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/audit-paie-marseille.webp"
        backgroundColor="bg-neutral-50"
        extraContent={
          <Button 
            variant="gradient" 
            onClick={() => setIsHubSpotModalOpen(true)}
            className="mt-8"
          >
            ⚡️ Auditer mes process
          </Button>
        }
      />

      <Section
        title="Onboarding & préboarding digitalisé"
        description="Augmentez de 33% vos taux de rétention grâce à un parcours collaborateur fluide et engageant dès l'acceptation de l'offre. Une solution d'externalisation RH efficace."
        bullets={[
          "Checklists automatiques",
          "Kits d'accueil digitalisés",
          "Feedback manager intégré"
        ]}
        image="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/onboarding-_1_.webp"
        backgroundColor="bg-white"
        extraContent={
          <Button 
            variant="gradient" 
            onClick={() => setIsHubSpotModalOpen(true)}
            className="mt-8"
          >
            ⚡️ Auditer mes process
          </Button>
        }
      />

      <Section
        reversed
        title="Conseil RH & IA sur-mesure"
        description="On développe des outils RH automatisés 100% adaptés à vos besoins : scoring candidats, matching culture, analyse de paie, dashboards RH, etc."
        bullets={[
          "Gains de temps supérieurs à 60%",
          "Outils IA internes clé en main",
          "Dashboards RH, onboarding & reporting personnalisés"
        ]}
        image="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Conseil-IA-marseille.webp"
        backgroundColor="bg-neutral-50"
        extraContent={
          <Button 
            variant="gradient" 
            onClick={() => setIsHubSpotModalOpen(true)}
            className="mt-8"
          >
            ⚡️ Auditer mes process
          </Button>
        }
      />

      <section className="py-20 text-center bg-gradient-primary text-white">
        <h2 className="text-3xl font-bold mb-4 font-title">🎯 Planifiez votre audit RH gratuit</h2>
        <p className="text-lg mb-6">En 30 minutes, identifiez vos axes d'optimisation RH & IA.</p>
        <Button
          variant="white"
          size="lg"
          onClick={() => window.location.href = 'https://calendar.app.google/ehHQymdfUYD7baYC9'}
        >
          Je réserve mon créneau →
        </Button>
      </section>

      <HubSpotModal
        isOpen={isHubSpotModalOpen}
        onClose={() => setIsHubSpotModalOpen(false)}
      />

      <section className="bg-white py-16 px-6 md:px-16">
        <div className="max-w-4xl mx-auto text-lg text-neutral-800 leading-relaxed">
          <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
            Externalisation RH à Marseille : simplicité, conformité, performance
          </h2>
          <p className="mb-4">
            Recruteurs.io propose une approche moderne et transparente de l'<strong>externalisation RH à Marseille</strong>. Notre mission : libérer les dirigeants, DRH ou Office Managers des tâches chronophages pour leur permettre de se concentrer sur leur cœur de métier. Nous accompagnons des entreprises locales, TPE/PME ou groupes, dans la structuration de leur fonction RH.
          </p>
          <p className="mb-4">
            Nos interventions couvrent la <a href="/audit" className="underline">mise en conformité</a> (DUERP, affichage obligatoire, droit du travail), l’<a href="/ressources/outils" className="underline">onboarding digitalisé</a>, la gestion de la paie externalisée, et même le déploiement d’<a href="/entreprise/ia" className="underline">solutions IA RH personnalisées</a>.
          </p>
          <p className="mb-4">
            Chaque prestation est packagée, mesurable, et commence par un <strong><a href="https://calendar.app.google/ehHQymdfUYD7baYC9" className="underline">audit RH gratuit</a></strong>. En choisissant Recruteurs.io, vous gagnez du temps, réduisez les risques, et professionnalisez votre gestion RH avec un partenaire local.
          </p>
          <p>
            Externaliser ses RH, ce n’est pas perdre le contrôle. C’est le reprendre. Avec Recruteurs.io, vous avez un partenaire fiable, proactif et implanté à Marseille pour vous accompagner durablement.
          </p>
        </div>
      </section>
    </Layout>
  );
}